<template>
    <section>
        <div class="header-section">
            <OrderCompanyInfo
                :company-profile="companyProfile"
            />

            <div class="number-container">
                <div v-if="hasTaxOptions" class="due-date-picker">
                    {{ $t('InvoiceHeader.header.taxinvoice') }}
                </div>

                <div class="invoice-number-title">
                    {{ $t('InvoiceHeader.header.invoice') }}
                </div>

                <div class="invoice-number">
                    {{ invoiceId }}
                </div>

                <div class="tax-details">
                    <div class="invoice-number-title">
                        {{ $t('InvoiceHeader.header.issueDate') }}
                    </div>

                    <div class="invoice-number">
                        {{ formattedIssueDate }}
                    </div>

                    <span>{{ taxTypeText }}</span>
                    <span class="invoice-number">{{ appSalesInfo.taxId }}</span>
                </div>

                <slot />

                <div v-if="showStatusPill" class="paid">
                    <OrderStatusPill :order-status="invoice.status" />
                </div>

                <div class="secure-transaction">
                    <DsIcon name="lock-locked-fill" />
                    <span>{{ $t('MoneyLib.secureTransaction') }}</span>
                </div>

                <div class="download-pdf">
                    <DsTextButton @click="print">
                        {{ $t('InvoiceHeader.header.printInvoice') }}
                    </DsTextButton>
                </div>

                <div v-if="showRefundText" class="refunded-note">
                    <DsInlineAlert leading-icon data-qa="refund-alert">
                        {{ $t('InvoiceHeader.refundNote') }}
                    </DsInlineAlert>
                </div>
            </div>
        </div>

        <div class="contact-information">
            <div class="contact-info">
                <div class="column-header">
                    {{ $t('MoneyLib.billedTo') }}
                </div>
                <DsCard>
                    <DsCardBody>
                        <InvoiceContactInfo
                            v-if="invoice.contact"
                            :contact-id="contactId"
                            :contact="invoice.contact"
                            :shipping-information="shippingInformation"
                            :status="invoice.status"
                        />
                    </DsCardBody>
                </DsCard>
            </div>

            <div class="due-date">
                <div class="column-header">
                    {{ $t('InvoiceHeader.header.issueDate') }}
                </div>

                <div class="due-date-picker">
                    <span>
                        {{ formattedIssueDate }}
                    </span>
                </div>

                <br />

                <div v-if="!isRecurring" class="column-header">
                    {{ $t('MoneyLib.dueBy') }}
                </div>

                <div v-if="!isRecurring" class="due-date-picker">
                    <span data-qa="invoices-display-date">
                        {{ formattedDueDate }}
                    </span>
                </div>
            </div>

            <div v-if="!hasDeposit" class="due-amount">
                <div class="column-header">
                    {{ $t('MoneyLib.totalDue') }}
                </div>

                <div class="total-due-number">
                    {{ $n(totalDue || 0, 'currency') }}
                </div>
            </div>

            <div v-else class="due-amount">
                <div class="column-header">
                    {{ $t('InvoiceHeader.header.depositDue') }}
                </div>

                <div class="total-due-number">
                    {{ $n(depositDue || 0, 'currency') }}
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import moment from 'moment';
import {
    DsCard,
    DsCardBody,
    DsIcon,
    DsInlineAlert,
    DsTextButton,
} from '@infusionsoft/design-system';
import { orderStatus } from '../../money.constants';
import { invoiceTotalDue, invoiceIsRefunded } from '../../money.util';
import OrderStatusPill from '../OrderStatusPill.vue';
import InvoiceContactInfo from './InvoiceContactInfo.vue';
import OrderCompanyInfo from '../order/OrderCompanyInfo.vue';
import { DISPLAY_DATE_FORMAT_WITH_YEAR } from '@keap-web/shared-ui';

export default {
    components: {
        DsCard,
        DsCardBody,
        DsIcon,
        DsInlineAlert,
        DsTextButton,
        OrderStatusPill,
        InvoiceContactInfo,
        OrderCompanyInfo,
    },

    props: {
        contactId: [Number, String],
        customerView: Boolean,
        companyProfile: {
            type: Object,
            default: () => {},
        },
        invoice: {
            type: Object,
            default: () => {
                return {
                    contact: {},
                };
            },
        },
        appSalesInfo: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        taxTypeText() {
            const { taxIdType } = this.appSalesInfo;

            if (taxIdType === 'AUD') return 'ABN #';
            if (taxIdType === 'GBP') return 'VAT #';

            return taxIdType;
        },

        hasTaxOptions() {
            const { taxIdType, taxId } = this.appSalesInfo;

            return Boolean(taxIdType && taxId);
        },

        invoiceId() {
            if (this.sequentialInvoiceEnabled) return this.invoice?.invoiceNumber;

            return this.invoice?.id || this.invoice?.orderId;
        },

        sequentialInvoiceEnabled() {
            return this.invoice?.featureFlags?.sequentialInvoiceId;
        },

        hasDeposit() {
            return this.invoice
                && this.invoice.payPlan
                && this.invoice.payPlan.amountDue
                && this.invoice.totalPaid < this.invoice.payPlan.amountDue;
        },

        depositDue() {
            return this.invoice.payPlan.amountDue - this.invoice.totalPaid;
        },

        formattedDueDate() {
            return moment(this.invoice.dueDate).format(DISPLAY_DATE_FORMAT_WITH_YEAR);
        },

        formattedIssueDate() {
            return moment(this.invoice?.dateCreated).format(DISPLAY_DATE_FORMAT_WITH_YEAR);
        },

        shippingInformation() {
            return this.invoice.shippingInformation || {};
        },

        totalDue() {
            return invoiceTotalDue(this.invoice);
        },

        showStatusPill() {
            return Boolean((this.invoice.status === orderStatus.PAID && invoiceTotalDue(this.invoice) <= 0)
                || invoiceIsRefunded(this.invoice)
                || this.invoice.status === orderStatus.DEPOSIT_PAID);
        },

        showRefundText() {
            return invoiceIsRefunded(this.invoice) && !this.customerView;
        },

        isRecurring() {
            return this.invoice.orderItems?.some(({ recurringBilling }) => recurringBilling);
        },
    },

    methods: {
        print() {
            window.print();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../styles/common';

.header-section {
    padding: $spacing-500 $spacing-500 0;
    margin-bottom: $spacing-400;
    display: flex;
    justify-content: space-between;

    .order-status {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }
}

.total-due-number {
    font-size: $font-size-xl;
}

.number-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .invoice-number {
        margin-bottom: $spacing-200;
        color: $color-gray-800;
    }
}

.contact-information {
    padding: $spacing-400 $spacing-500 $spacing-300;
    display: flex;
    justify-content: space-between;
    flex: 9;
}

.contact-info {
    width: px-to-rem(300px);
}

.column-header {
    margin-bottom: $spacing-100;
    font-weight: $font-weight-bold;
}

.due-amount {
    max-width: 41%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    div {
        display: flex;
        justify-content: flex-end;
    }
}

.download-pdf {
    margin-top: $spacing-100;
}

.due-date-picker {
    font-size: $font-size-xl;
}

.refunded-note {
    margin-top: $spacing-100;
    @include text-align-end;
    align-self: flex-end;
    font-size: $font-size-xs;
    max-width: px-to-rem(479px);
    text-align: left;
}

.secure-transaction {
    --icon-size: #{px-to-rem(12px)};
    --icon-margin: #{0 $spacing-050 (-$spacing-025) 0};
}

@media print {
    .secure-transaction {
        display: none;
    }

    .contact-information {
        flex-direction: row;
        padding: 0 $spacing-500 $spacing-300;
    }

    .due-amount {
        .pay-now,
        .accept-quote,
        .download-pdf {
            display: none;
        }
    }

    .number-container .accept-quote,
    .header-section .pay-now,
    .mobile .accept-quote,
    .number-container .download-pdf,
    .number-container .refunded-note {
        display: none;
    }
}

@media only screen and ($small) {
    .header-section {
        padding: $spacing-200 $spacing-200 0 $spacing-200;
        flex-direction: column;
        margin: 0;

        .invoice-number-title,
        .invoice-number,
        .download-pdf,
        .order-status-pill:not(.accepted):not(.paid):not(.acceptedInvoiced):not(.refunded):not(.depositPaid),
        .accept-quote,
        .secure-transaction,
        .pay-now {
            display: none;
        }

        .number-container,
        .refunded-note {
            @include text-align-start;
            justify-content: flex-start;
            align-items: flex-start;
        }

        .order-status {
            align-items: flex-start;
        }

        .order-status,
        .depositPaid,
        .paid {
            margin-top: $spacing-200;
        }
    }

    .contact-information {
        padding: $spacing-200;
        flex-direction: column;
        align-items: flex-start;
        border-top: 0;

        &:not(.editing) .quote-due-date {
            display: none;
        }
    }

    .contact-info {
        width: 100%;
        --input-margin-bottom: 0;
    }

    .due-date {
        width: 100%;
        margin-top: $spacing-200;
    }

    .due-amount {
        display: none;
    }
}

.tax-details {
    display: flex;
    flex-direction: column;
    text-align: right;
}

[dir=rtl] {
    .refunded-note {
        text-align: right;
    }
}
</style>
